/* eslint-disable react/button-has-type */ // Type is given by callee
import React from 'react';

import { Placement } from '@floating-ui/react';
import classNames, { Argument } from 'classnames';

import Spinner from '@travauxlib/shared/src/components/DesignSystem/assets/Spinner.svg?react';
import { toast } from '@travauxlib/shared/src/components/Notifications';

import { ActionDisabledWithTooltip } from './ActionDisabledWithTooltip';

export type IButtonVariants =
  | 'primary'
  | 'ghost'
  | 'secondary'
  | 'tertiary'
  | 'primaryOutline'
  | 'secondaryApp'
  | 'primaryOutlineApp';

export type IButtonSizes = 'sm' | 'md' | 'lg' | 'xs';

export interface ButtonProps {
  variant?: IButtonVariants;
  size?: IButtonSizes;
  leftIcon?: React.ReactElement;
  rightIcon?: React.ReactElement;
  fullwidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  disabledReason?: string;
  disabledMessageTooltip?: false | string;
  disabledMessageTooltipSmall?: boolean;
  disabledTriggerClassName?: string;
  positionTooltip?: Placement;
  widthTooltip?: string;
  fixedFloating?: boolean;
  responsive?: boolean;
  children?: React.ReactNode;
  hiddenLabelDuringLoading?: boolean;
}

type DataAttributeKey = `data-${string}`;
export type ICommonButtonProps = ButtonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    [dataAttribute: DataAttributeKey]: string;
  };

export const makeVariantButtonClassnames = (
  disabled?: boolean,
): { [key in IButtonVariants]: Argument[] } => ({
  ghost: [
    'outline-0 border-0',
    'hover:action-bg-neutral-hover hover:text-brand',
    'active:bg-transparent active:text-brand',
    'focus:action-bg-neutral-pressed',
    { 'bg-transparent! outline-0 text-disabled!': disabled },
    { 'text-brand hover:text-brand bg-transparent': !disabled },
  ],
  primary: [
    'outline-0 border-0',
    'hover:action-bg-primary-hover',
    'active:action-bg-primary-pressed',
    { 'action-bg-primary-disabled!': disabled },
    { 'text-inverse hover:text-inverse action-bg-primary-default': !disabled },
  ],
  primaryOutline: [
    'outline-0 border-solid border-[1px] border-brand',
    'hover:action-bg-primary-default hover:text-inverse',
    'active:action-bg-primary-hover',
    { 'action-bg-primary-disabled': disabled },
    { 'text-brand action-bg-neutral-default': !disabled },
  ],
  secondary: [
    'outline border-0 outline-secondary',
    {
      'action-bg-neutral-disabled! outline-disabled! text-disabled!': disabled,
    },
    {
      'text-blue action-bg-neutral-default hover:action-bg-secondary-hover hover:text-inverse hover:action-outline-secondary-hover active:action-bg-secondary-pressed active:text-inverse active:action-outline-secondary-pressed':
        !disabled,
    },
  ],
  tertiary: [
    'outline-0 border-0',
    'bg-transparent',
    'hover:action-bg-neutral-hover hover:text-primary',
    'active:action-bg-neutral-pressed active:text-primary',
    'focus:action-bg-neutral-hover',
    {
      'action-bg-neutral-default! outline-0 text-disabled!': disabled,
    },
    { 'text-primary': !disabled },
  ],
  primaryOutlineApp: [
    'outline-0 border-solid border-[1px] border-brand',
    'hover:text-brand text-brand hover:action-bg-neutral-default',
    { 'action-bg-primary-disabled': disabled },
  ],
  secondaryApp: [
    'rounded-full! outline text-secondary',
    'hover:action-bg-neutral-hover hover:text-secondary',
    { 'action-bg-neutral-disabled! text-disabled': disabled },
  ],
});

const makeResponsiveButtonClassnames = ({
  isIconOnlyButton,
}: {
  isIconOnlyButton?: boolean;
}): Argument[] => [
  { 'sm-desktop:text-b2 sm-desktop:px-md sm-desktop:py-sm': !isIconOnlyButton },
  { 'sm-desktop:text-b2 sm-desktop:p-sm': isIconOnlyButton },
  { 'text-b3 px-xs py-[0.375rem]': !isIconOnlyButton },
  { 'text-b3 p-xs': isIconOnlyButton },
];

const makeCommonButtonClassnames = ({
  size,
  isIconOnlyButton,
  isFullwidth,
  disabled,
}: {
  size?: IButtonSizes;
  isIconOnlyButton?: boolean;
  isFullwidth?: boolean;
  disabled?: boolean;
}): Argument[] => [
  { 'text-cta-lg px-lg py-md': size === 'lg' && !isIconOnlyButton },
  { 'text-cta-lg p-md': size === 'lg' && isIconOnlyButton },
  { 'text-b2 px-md py-sm': size === 'md' && !isIconOnlyButton },
  { 'text-b2 p-sm': size === 'md' && isIconOnlyButton },
  { 'text-b3 px-xs py-[0.375rem]': size === 'sm' && !isIconOnlyButton },
  { 'text-b3 p-xs': size === 'sm' && isIconOnlyButton },
  { 'w-full flex justify-center': isFullwidth },
  { 'cursor-not-allowed text-disabled': disabled },
  'font-normal',
  // 'focus-visible:outline-2', // Replace by border?
  'rounded-xxs',
  'hover:no-underline',
];

interface IButtonLeftIconContainerProps {
  icon: React.ReactElement;
  left?: boolean;
  size: IButtonSizes;
  isIconOnlyButton?: boolean;
}

export const ButtonIconContainer = ({
  icon,
  left,
  size,
  isIconOnlyButton,
}: IButtonLeftIconContainerProps): React.ReactElement => (
  <div
    className={classNames('flex items-center shrink-0', {
      'w-xl': size === 'lg',
      'w-lg': size === 'md',
      'w-md': size === 'sm',
      'mr-xxs -ml-xxs': left && !isIconOnlyButton,
      '-mr-xxs ml-xxs ': !left && !isIconOnlyButton,
    })}
  >
    {icon}
  </div>
);

type IButtonContentProps = {
  leftIcon?: React.ReactElement;
  rightIcon?: React.ReactElement;
  size: IButtonSizes;
  fullwidth?: boolean;
  className?: string;
  children?: React.ReactNode;
  hiddenLabelDuringLoading?: boolean;
};

export const ButtonContent: React.FC<IButtonContentProps> = ({
  className,
  leftIcon,
  rightIcon,
  size,
  children,
  fullwidth,
}) => (
  <div
    className={classNames(className, 'flex items-center justify-center', {
      'flex-grow': fullwidth,
    })}
  >
    {leftIcon && (
      <ButtonIconContainer icon={leftIcon} size={size} isIconOnlyButton={!children} left />
    )}
    {children}
    {rightIcon && <ButtonIconContainer icon={rightIcon} size={size} isIconOnlyButton={!children} />}
  </div>
);

export const makeButtonClassnames = ({
  size = 'md',
  leftIcon,
  rightIcon,
  children,
  fullwidth,
  variant,
  disabled,
  responsive,
}: ButtonProps): Argument[] => [
  ...makeCommonButtonClassnames({
    size,
    isIconOnlyButton: !!((leftIcon || rightIcon) && !children),
    isFullwidth: fullwidth,
    disabled,
  }),
  ...makeVariantButtonClassnames(disabled)[variant || 'primary'],
  responsive &&
    makeResponsiveButtonClassnames({
      isIconOnlyButton: !!((leftIcon || rightIcon) && !children),
    }),
];

export const Button = React.forwardRef<HTMLButtonElement, ICommonButtonProps>(
  (
    {
      variant,
      size = 'md',
      leftIcon,
      rightIcon,
      fullwidth,
      type = 'button',
      className,
      children,
      loading = false,
      onClick,
      disabledReason,
      disabledMessageTooltip,
      disabledMessageTooltipSmall,
      disabledTriggerClassName,
      positionTooltip,
      widthTooltip,
      responsive,
      fixedFloating,
      hiddenLabelDuringLoading = false,
      ...rest
    },
    ref,
  ) => (
    <ActionDisabledWithTooltip
      disabled={rest.disabled}
      disabledMessage={disabledMessageTooltip}
      small={disabledMessageTooltipSmall}
      disabledTriggerClassName={disabledTriggerClassName}
      position={positionTooltip}
      widthTooltip={widthTooltip}
      fixedFloating={fixedFloating}
      fullwidth={fullwidth}
    >
      <button
        className={classNames(
          'relative',
          className,
          makeButtonClassnames({
            size,
            leftIcon,
            rightIcon,
            variant,
            fullwidth,
            children,
            disabled: rest.disabled,
            responsive: responsive,
          }),
        )}
        ref={ref}
        type={type}
        onClick={event => {
          if (disabledReason) {
            event.preventDefault();
            return toast.info('Action bloquée', { message: disabledReason });
          } else {
            return onClick?.(event);
          }
        }}
        {...rest}
      >
        <ButtonContent
          fullwidth={fullwidth}
          leftIcon={hiddenLabelDuringLoading && loading ? undefined : leftIcon}
          rightIcon={
            loading ? <Spinner className="animate-spin" data-testid="loading-icon" /> : rightIcon
          }
          size={size}
          hiddenLabelDuringLoading={hiddenLabelDuringLoading}
        >
          {hiddenLabelDuringLoading && loading ? null : children}
        </ButtonContent>
      </button>
    </ActionDisabledWithTooltip>
  ),
);
